import React, { useState, useMemo } from "react";
import {
    useGetCreditSharesQuery,
    useDeleteCreditShareMutation,
    CreditShare,
} from "../api";
import { CreditSharesModal } from "../components/CreditSharesModal";
import { TransferCreditSharesForm } from "../components/TransferCreditSharesForm";
import { useNotification } from "../hooks/useNotification";
import Notification from "../components/ui/Notification";
import { UserAutocomplete } from "../components/UserAutocomplete";
import { useSettings } from "../api/users";

type SortField = "quantity" | "status" | "next_payment_date" | "paid" | null;
type SortDirection = "asc" | "desc";
type StatusFilter = "all" | "active" | "overdue";

export const CreditSharesPage: React.FC = () => {
    const { showError, showSuccess, notification, hideNotification } =
        useNotification();
    const [selectedCreditShare, setSelectedCreditShare] =
        useState<CreditShare | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState<"create" | "edit" | "delete">("edit");

    // Фильтры и сортировка
    const [searchUserId, setSearchUserId] = useState<string>("");
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
    const [sortField, setSortField] = useState<SortField>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

    const {
        data: creditSharesResponse,
        isLoading,
        refetch,
    } = useGetCreditSharesQuery();

    const { data: settings } = useSettings();

    const [deleteCreditShare, { isLoading: isDeleting }] =
        useDeleteCreditShareMutation();

    const allCreditShares = creditSharesResponse?.data || [];

    // Получаем цену акции из настроек
    const pricePerShare = useMemo(() => {
        if (!settings) return 100; // Значение по умолчанию
        const sharesConfigSetting = settings.find(
            (s) => s.key === "shares_config"
        );
        if (!sharesConfigSetting) return 100;

        try {
            const config =
                typeof sharesConfigSetting.value === "string"
                    ? JSON.parse(sharesConfigSetting.value)
                    : sharesConfigSetting.value;
            return config?.price_share || 100;
        } catch {
            return 100;
        }
    }, [settings]);

    // Функция для расчета процента оплаты
    const calculatePaymentPercent = (creditShare: CreditShare): number => {
        if (creditShare.quantity === 0 || pricePerShare === 0) return 0;
        const totalCost = creditShare.quantity * pricePerShare;
        const payed = creditShare.payed ?? 0;
        if (totalCost === 0) return 0;
        return Math.min(100, Math.round((payed / totalCost) * 100));
    };

    const handleCreate = () => {
        setSelectedCreditShare(null);
        setModalMode("create");
        setIsModalOpen(true);
    };

    const handleEdit = (creditShare: CreditShare) => {
        setSelectedCreditShare(creditShare);
        setModalMode("edit");
        setIsModalOpen(true);
    };

    const handleDelete = (creditShare: CreditShare) => {
        setSelectedCreditShare(creditShare);
        setModalMode("delete");
        setIsModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedCreditShare) return;

        try {
            await deleteCreditShare(selectedCreditShare.user_id).unwrap();
            showSuccess("Кредитные акции удалены успешно");
            setIsModalOpen(false);
            setSelectedCreditShare(null);
            refetch();
        } catch (error: any) {
            showError(
                error?.data?.message ||
                    error?.message ||
                    "Ошибка при удалении кредитных акций"
            );
        }
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedCreditShare(null);
    };

    const formatPaymentDate = (dateStr: string | null): string => {
        if (!dateStr) return "—";
        return new Date(dateStr).toLocaleDateString("ru-RU", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        });
    };

    // Фильтрация и сортировка
    const filteredAndSortedShares = useMemo(() => {
        let filtered = [...allCreditShares];

        // Фильтр по пользователю
        if (searchUserId) {
            const userId = parseInt(searchUserId);
            if (!isNaN(userId)) {
                filtered = filtered.filter(
                    (share) => share.user_id === userId
                );
            }
        }

        // Фильтр по статусу
        if (statusFilter !== "all") {
            filtered = filtered.filter((share) => {
                if (statusFilter === "active") {
                    return !share.is_payment_due;
                }
                if (statusFilter === "overdue") {
                    return share.is_payment_due;
                }
                return true;
            });
        }

        // Сортировка
        if (sortField) {
            filtered.sort((a, b) => {
                let aValue: any;
                let bValue: any;

                switch (sortField) {
                    case "quantity":
                        aValue = a.quantity;
                        bValue = b.quantity;
                        break;
                    case "status":
                        aValue = a.is_payment_due ? 1 : 0;
                        bValue = b.is_payment_due ? 1 : 0;
                        break;
                    case "next_payment_date":
                        aValue = a.next_payment_date
                            ? new Date(a.next_payment_date).getTime()
                            : 0;
                        bValue = b.next_payment_date
                            ? new Date(b.next_payment_date).getTime()
                            : 0;
                        break;
                    case "paid":
                        aValue = a.paid;
                        bValue = b.paid;
                        break;
                    default:
                        return 0;
                }

                if (aValue < bValue) {
                    return sortDirection === "asc" ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortDirection === "asc" ? 1 : -1;
                }
                return 0;
            });
        }

        return filtered;
    }, [allCreditShares, searchUserId, statusFilter, sortField, sortDirection]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection("asc");
        }
    };

    const getSortIcon = (field: SortField) => {
        if (sortField !== field) {
            return (
                <svg
                    className="w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                    />
                </svg>
            );
        }
        return sortDirection === "asc" ? (
            <svg
                className="w-4 h-4 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 15l7-7 7 7"
                />
            </svg>
        ) : (
            <svg
                className="w-4 h-4 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                />
            </svg>
        );
    };

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Кредитные ваучеры
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Управление кредитными акциями пользователей
                </p>
            </div>

            <div className="flex justify-end mb-6">
                <button
                    onClick={handleCreate}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors shadow-sm"
                >
                    <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                        />
                    </svg>
                    Добавить запись
                </button>
            </div>

            <TransferCreditSharesForm
                onSuccess={() => {
                    refetch();
                }}
            />

            {/* Фильтры и поиск */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Поиск по пользователю
                        </label>
                        <UserAutocomplete
                            value={searchUserId}
                            onChange={setSearchUserId}
                            placeholder="Введите ID, email, имя..."
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Статус
                        </label>
                        <select
                            value={statusFilter}
                            onChange={(e) =>
                                setStatusFilter(e.target.value as StatusFilter)
                            }
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                            <option value="all">Все</option>
                            <option value="active">Активно</option>
                            <option value="overdue">Просрочено</option>
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => {
                                setSearchUserId("");
                                setStatusFilter("all");
                                setSortField(null);
                            }}
                            className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                            Сбросить фильтры
                        </button>
                    </div>
                </div>
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            ) : filteredAndSortedShares.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-8 text-center">
                    <p className="text-gray-500 dark:text-gray-400">
                        {allCreditShares.length === 0
                            ? "Нет пользователей с кредитными акциями"
                            : "Не найдено записей по заданным фильтрам"}
                    </p>
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Пользователь
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        <button
                                            onClick={() => handleSort("quantity")}
                                            className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                                        >
                                            Количество акций
                                            {getSortIcon("quantity")}
                                        </button>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Сумма платежа
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        День платежа
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        <button
                                            onClick={() => handleSort("paid")}
                                            className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                                        >
                                            Оплачено
                                            {getSortIcon("paid")}
                                        </button>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        <button
                                            onClick={() =>
                                                handleSort("next_payment_date")
                                            }
                                            className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                                        >
                                            Следующий платеж
                                            {getSortIcon("next_payment_date")}
                                        </button>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        <button
                                            onClick={() => handleSort("status")}
                                            className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                                        >
                                            Статус
                                            {getSortIcon("status")}
                                        </button>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Иммунитет
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Действия
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {filteredAndSortedShares.map((creditShare) => (
                                    <tr
                                        key={creditShare.user_id}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-700"
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                {creditShare.user
                                                    ? `${creditShare.user.name} ${creditShare.user.surname}`
                                                    : `ID: ${creditShare.user_id}`}
                                            </div>
                                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                                {creditShare.user?.email || "—"}
                                            </div>
                                            <div className="text-xs text-gray-400 dark:text-gray-500">
                                                ID: {creditShare.user_id}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            {creditShare.quantity}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            {creditShare.money} ₮
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            {creditShare.day}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            ${(creditShare.payed ?? 0).toFixed(2)} (
                                            {calculatePaymentPercent(creditShare)}%)
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            {formatPaymentDate(
                                                creditShare.next_payment_date
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {creditShare.is_payment_due ? (
                                                <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100">
                                                    Просрочено
                                                </span>
                                            ) : (
                                                <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
                                                    Активно
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            {creditShare.immunity ? (
                                                <span className="text-green-600 dark:text-green-400 font-medium">
                                                    Есть
                                                </span>
                                            ) : (
                                                <span className="text-gray-500 dark:text-gray-400">
                                                    Нет
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() =>
                                                        handleEdit(creditShare)
                                                    }
                                                    className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                                                    title="Редактировать"
                                                >
                                                    <svg
                                                        className="w-5 h-5"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                                        />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        handleDelete(creditShare)
                                                    }
                                                    disabled={isDeleting}
                                                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 transition-colors disabled:opacity-50"
                                                    title="Удалить"
                                                >
                                                    <svg
                                                        className="w-5 h-5"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                        />
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {isModalOpen && (
                <CreditSharesModal
                    isOpen={isModalOpen}
                    onClose={handleModalClose}
                    creditShare={selectedCreditShare}
                    mode={modalMode}
                    onDelete={handleDeleteConfirm}
                    onSuccess={() => {
                        refetch();
                        handleModalClose();
                    }}
                />
            )}

            <Notification
                message={notification.message}
                type={notification.type}
                isVisible={notification.isVisible}
                onClose={hideNotification}
            />
        </div>
    );
};

